import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MomLayout from './MomLayout';
import { useMomAuth } from '../../context/MomAuthContext';
import { generationApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Sparkles, Loader2, Heart, X, Download,
  Baby, User as UserIcon, RefreshCw
} from 'lucide-react';
import { InsufficientCreditsModal } from '../../components/credits/InsufficientCreditsModal';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const stripPrefix = (dataUrl) => (dataUrl?.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl);

const PhotoTile = ({ label, image, onUpload, onRemove, accent, testid }) => (
  <div className="space-y-2">
    <p className="text-xs font-medium text-[#7A6C68]">{label}</p>
    <div
      className={`relative aspect-square rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all ${
        image ? 'border-transparent bg-white shadow-sm' : 'border-[#F3D8D0] bg-[#FFF6F3]'
      }`}
      data-testid={testid}
    >
      {image ? (
        <>
          <img src={image} alt={label} className="w-full h-full object-cover" />
          <button
            onClick={onRemove}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
            data-testid={`${testid}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </>
      ) : (
        <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-center px-3 gap-2">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}>
            <Upload className="w-5 h-5" />
          </div>
          <p className="text-xs text-[#7A6C68] font-medium">Fotoğraf seç</p>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onUpload}
            data-testid={`${testid}-input`}
          />
        </label>
      )}
    </div>
  </div>
);

export default function MomAIFacePage() {
  const navigate = useNavigate();
  const { mom } = useMomAuth();

  const [motherDataUrl, setMotherDataUrl] = useState(null);
  const [fatherDataUrl, setFatherDataUrl] = useState(null);
  const [gender, setGender] = useState(mom?.baby_gender && mom.baby_gender !== 'unknown' ? mom.baby_gender : 'female');
  const [generating, setGenerating] = useState(false);
  const [resultDataUrl, setResultDataUrl] = useState(null);
  const [inheritedTraits, setInheritedTraits] = useState([]);
  const [creditsError, setCreditsError] = useState(null);

  const handleFile = (setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fotoğraf 10MB\'den küçük olmalı');
      return;
    }
    const dataUrl = await fileToBase64(file);
    setter(dataUrl);
  };

  const canGenerate = !!motherDataUrl && !!fatherDataUrl && !generating;

  const onGenerate = useCallback(async () => {
    if (!motherDataUrl || !fatherDataUrl) {
      toast.error('Anne ve baba fotoğrafı zorunludur.');
      return;
    }
    setGenerating(true);
    setResultDataUrl(null);
    setInheritedTraits([]);
    setCreditsError(null);
    try {
      const res = await generationApi.generateAiBabyFace({
        gender,
        pregnancy_week: mom?.pregnancy_week || 23,
        patient_name: `${mom?.first_name || ''} ${mom?.last_name || ''}`.trim(),
        mother_photo_base64: stripPrefix(motherDataUrl),
        father_photo_base64: stripPrefix(fatherDataUrl),
      });
      if (res?.image_base64) {
        setResultDataUrl(`data:image/png;base64,${res.image_base64}`);
        setInheritedTraits(res.inherited_traits || []);
        toast.success('Bebek yüzü oluşturuldu!');
      } else {
        toast.error('Görsel üretilemedi, lütfen tekrar deneyin.');
      }
    } catch (err) {
      if (err?.status === 402 && err?.detail) {
        setCreditsError(err.detail);
      } else if (err?.status === 422) {
        toast.error(err.message || 'Anne ve baba fotoğrafı zorunludur.');
      } else {
        toast.error(err?.message || 'Bir hata oluştu');
      }
    } finally {
      setGenerating(false);
    }
  }, [motherDataUrl, fatherDataUrl, gender, mom]);

  const onDownload = () => {
    if (!resultDataUrl) return;
    const a = document.createElement('a');
    a.href = resultDataUrl;
    a.download = `bebek_${(mom?.first_name || 'mom')}_${new Date().toISOString().split('T')[0]}.png`;
    a.click();
  };

  const onReset = () => {
    setResultDataUrl(null);
    setInheritedTraits([]);
    setMotherDataUrl(null);
    setFatherDataUrl(null);
  };

  return (
    <MomLayout>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => navigate('/mom/dashboard')}
          className="w-10 h-10 rounded-2xl bg-white border border-[#F3E5E0] flex items-center justify-center text-[#7A6C68] hover:text-[#FB7185]"
          data-testid="mom-aiface-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-center flex-1 px-3">
          <p className="text-xs text-[#7A6C68]">Genetik Füzyon</p>
          <h1 className="text-base font-bold text-[#2D2422]">AI Bebek Yüzü</h1>
        </div>
        <div className="w-10" />
      </div>

      {!resultDataUrl ? (
        <>
          {/* Info hero */}
          <div
            className="rounded-3xl p-5 mb-5 shadow-sm relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #FB7185, #F472B6)' }}
            data-testid="mom-aiface-hero"
          >
            <Sparkles className="absolute top-4 right-4 w-5 h-5 text-white/40" />
            <p className="text-white/85 text-xs font-medium mb-1">Bebek nasıl görünecek?</p>
            <h2 className="text-xl font-bold text-white leading-snug mb-2">
              Anne &amp; baba fotoğrafından bebeğinin AI portresini oluştur
            </h2>
            <p className="text-white/90 text-xs leading-relaxed">
              Genetik füzyon teknolojisi ile ten rengi, saç ve yüz hatlarını birleştiriyoruz.
              Her üretim <span className="font-semibold">1 kredi</span> tüketir.
            </p>
          </div>

          {/* Photo upload */}
          <p className="text-sm font-semibold text-[#2D2422] mb-2">Fotoğrafları yükle <span className="text-[#FB7185]">*</span></p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <PhotoTile
              label="Anne"
              image={motherDataUrl}
              onUpload={handleFile(setMotherDataUrl)}
              onRemove={() => setMotherDataUrl(null)}
              accent="bg-[#FFE4DF] text-[#FB7185]"
              testid="mom-aiface-mother"
            />
            <PhotoTile
              label="Baba"
              image={fatherDataUrl}
              onUpload={handleFile(setFatherDataUrl)}
              onRemove={() => setFatherDataUrl(null)}
              accent="bg-[#DBEAFE] text-[#3B82F6]"
              testid="mom-aiface-father"
            />
          </div>

          {/* Gender */}
          <p className="text-sm font-semibold text-[#2D2422] mb-2">Cinsiyet</p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => setGender('female')}
              className={`rounded-2xl p-3 border text-sm font-medium transition-all ${
                gender === 'female'
                  ? 'bg-[#FB7185] border-[#FB7185] text-white shadow-sm'
                  : 'bg-white border-[#F3E5E0] text-[#7A6C68]'
              }`}
              data-testid="mom-aiface-gender-female"
            >
              Kız
            </button>
            <button
              type="button"
              onClick={() => setGender('male')}
              className={`rounded-2xl p-3 border text-sm font-medium transition-all ${
                gender === 'male'
                  ? 'bg-[#3B82F6] border-[#3B82F6] text-white shadow-sm'
                  : 'bg-white border-[#F3E5E0] text-[#7A6C68]'
              }`}
              data-testid="mom-aiface-gender-male"
            >
              Erkek
            </button>
          </div>

          {/* Generate */}
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className={`w-full h-14 rounded-2xl text-white font-semibold shadow-sm transition-all flex items-center justify-center gap-2 ${
              canGenerate
                ? 'bg-gradient-to-r from-[#FB7185] to-[#F472B6] hover:shadow-md active:scale-[0.98]'
                : 'bg-[#E5DAD6] cursor-not-allowed'
            }`}
            data-testid="mom-aiface-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Oluşturuluyor...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                AI Bebek Yüzü Oluştur · 1 kredi
              </>
            )}
          </button>

          {(!motherDataUrl || !fatherDataUrl) && (
            <p className="text-xs text-[#FB7185] mt-3 text-center" data-testid="mom-aiface-validation-hint">
              Lütfen hem anne hem baba fotoğrafını yükleyin.
            </p>
          )}
        </>
      ) : (
        /* Result view */
        <div className="space-y-4" data-testid="mom-aiface-result">
          <div className="rounded-3xl bg-white p-5 shadow-sm border border-[#F3E5E0]">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-[#FB7185]/30 to-[#F472B6]/30 rounded-3xl blur-xl" />
              <img
                src={resultDataUrl}
                alt="Bebek"
                className="relative w-full aspect-square object-cover rounded-2xl"
                data-testid="mom-aiface-result-image"
              />
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {motherDataUrl && (
                <div className="text-center">
                  <img src={motherDataUrl} alt="Anne" className="w-14 h-14 rounded-full object-cover border-2 border-[#FB7185]/40" />
                  <p className="text-[10px] text-[#FB7185] mt-1 font-medium">Anne</p>
                </div>
              )}
              <div className="self-center">
                <Sparkles className="w-5 h-5 text-[#F472B6]" />
              </div>
              {fatherDataUrl && (
                <div className="text-center">
                  <img src={fatherDataUrl} alt="Baba" className="w-14 h-14 rounded-full object-cover border-2 border-[#3B82F6]/40" />
                  <p className="text-[10px] text-[#3B82F6] mt-1 font-medium">Baba</p>
                </div>
              )}
            </div>
          </div>

          {inheritedTraits.length > 0 && (
            <div className="rounded-3xl bg-white p-5 shadow-sm border border-[#F3E5E0]" data-testid="mom-aiface-traits">
              <p className="text-sm font-semibold text-[#2D2422] mb-2 flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#FB7185]" />
                Genetik Özellikler
              </p>
              <ul className="space-y-1.5">
                {inheritedTraits.map((t, i) => (
                  <li key={i} className="text-xs text-[#7A6C68] flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FB7185] mt-1.5 flex-none" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onDownload}
              className="rounded-2xl bg-white border border-[#F3E5E0] py-3 text-sm font-medium text-[#2D2422] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-aiface-download-btn"
            >
              <Download className="w-4 h-4" />
              İndir
            </button>
            <button
              onClick={onReset}
              className="rounded-2xl bg-[#FFE4DF] py-3 text-sm font-medium text-[#FB7185] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-aiface-reset-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Yenisini Üret
            </button>
          </div>
        </div>
      )}

      <InsufficientCreditsModal
        open={!!creditsError}
        onClose={() => setCreditsError(null)}
        detail={creditsError}
      />
    </MomLayout>
  );
}
